import { useRef, useState } from 'react'
import { Command, CommandGroup, CommandItem, CommandList } from './command'
import { Badge } from './badge'
import { Command as CommandPrimitive } from 'cmdk'
import { Check, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import Card from 'app/frontend/base/components/Card'

interface Props<T> {
  options: { label: React.ReactNode; value: T }[]
  value: T[]
  placeholder?: string
  onChange: (value: T[]) => void
  className?: string
  selectedDisplay?: 'badges' | 'count'
}

export function MultiSelect<T>({
  options,
  value,
  placeholder,
  onChange,
  className,
  selectedDisplay = 'badges',
}: Props<T>) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')

  const selected = options.filter((option) => value.includes(option.value))

  const handleUnselect = (option: Props<T>['options'][number]) => {
    onChange(value.filter((s) => s !== option.value))
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const input = inputRef.current
    if (input) {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (input.value === '') {
          const newSelected = [...value]
          newSelected.pop()
          onChange(newSelected)
        }
      }
      // This is not a default behaviour of the <input /> field
      if (e.key === 'Escape') {
        input.blur()
      }
    }
  }

  const selectables = options.filter((option) => !value.includes(option.value))
  const isCountDisplay = selectedDisplay === 'count'
  const visibleOptions = isCountDisplay ? options : selectables

  return (
    <Command
      onKeyDown={handleKeyDown}
      className={cn('overflow-visible bg-transparent h-10', className)}
    >
      <Card
        className='group text-sm focus-within:ring-1 focus-within:ring-ring'
        size='sm'
        onMouseDown={(e) => {
          if (
            e.target instanceof Element &&
            e.target.closest('button') !== null
          ) {
            return
          }
          e.preventDefault()
          inputRef.current?.focus()
          setOpen(true)
        }}
      >
        <div
          className={cn(
            'flex gap-1',
            isCountDisplay ? 'items-center flex-nowrap' : 'flex-wrap',
          )}
        >
          {isCountDisplay && selected.length > 0 ? (
            <span className='ml-2 text-foreground whitespace-nowrap'>
              {selected.length === 1
                ? selected[0].label
                : `${selected.length} selected`}
            </span>
          ) : null}
          {isCountDisplay
            ? null
            : selected.map((option) => {
                return (
                  <Badge
                    key={option.value as unknown as string}
                    variant='outline'
                  >
                    {option.label}
                    <button
                      className='ml-1 rounded-full outline-hidden focus:ring-1 focus:ring-ring'
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleUnselect(option)
                        }
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                      onClick={() => handleUnselect(option)}
                    >
                      <X className='h-3 w-3 text-muted-foreground hover:text-foreground' />
                    </button>
                  </Badge>
                )
              })}
          <CommandPrimitive.Input
            ref={inputRef}
            value={inputValue}
            onValueChange={setInputValue}
            onBlur={() => setOpen(false)}
            onFocus={() => setOpen(true)}
            placeholder={
              selected.length === 0 ? placeholder ?? 'Select options...' : ''
            }
            className='ml-2 flex-1 min-w-8 bg-transparent outline-hidden placeholder:text-muted-foreground'
          />
          {isCountDisplay && selected.length > 0 ? (
            <button
              className='ml-2 mr-[-0.3rem] rounded-full outline-hidden focus:ring-1 focus:ring-ring'
              onMouseDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
              onClick={() => {
                setInputValue('')
                onChange([])
              }}
            >
              <X className='opacity-50 hover:opacity-100 transition-opacity p-1' />
            </button>
          ) : null}
        </div>
      </Card>
      <div className='relative'>
        <CommandList>
          {open && visibleOptions.length > 0 ? (
            <div className='absolute top-2 z-10 w-full rounded-md border bg-popover text-popover-foreground shadow-md outline-hidden animate-in'>
              <CommandGroup className='h-full overflow-auto'>
                {visibleOptions.map((option) => {
                  const selectedOption = value.includes(option.value)
                  return (
                    <CommandItem
                      key={option.value as unknown as string}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                      onSelect={() => {
                        setInputValue('')
                        if (selectedOption) {
                          handleUnselect(option)
                        } else {
                          onChange([...value, option.value])
                        }
                      }}
                      className={'cursor-pointer'}
                    >
                      <div className='flex w-full items-center justify-between gap-2'>
                        <span>{option.label}</span>
                        {selectedOption ? <Check className='h-4 w-4' /> : null}
                      </div>
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </div>
          ) : null}
        </CommandList>
      </div>
    </Command>
  )
}
